import { Col, Form, message, Row } from "antd";
import { routes } from "common/routes";
import { IVendorMetaData, IVendorPurchaseCode } from "common/interface/IVendor";
import { AppPageHeader } from "components/AppPageHeader";
import { VendorForm, VENDOR_CITY_OPTIONS } from "forms/VendorForm";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getVendorById, submitVendorAmendment } from "store/vendor/vendorSlice";
import { getWarehouseId } from "services/PersistService/PersistWareHouseReturn";
import { getVendorSkuMappings } from "store/vendor-items/vendorItemsSlice";

const MESSAGE_KEY = "edit-vendor";

const toNumberOrUndefined = (val: any): number | undefined => {
  if (val === null || val === undefined || val === "") return undefined;
  const num = Number(val);
  return Number.isNaN(num) ? undefined : num;
};

const EditVendorPage = () => {
  const [form] = Form.useForm();
  const { vendors, status, error, amendmentStatus, amendmentError } = useAppSelector((state) => state.vendors);
  const [vendor, setVendor] = useState<any>();
  const [originalMetadata, setOriginalMetadata] = useState<IVendorMetaData>();
  // Mappings fetched from /api/v1/vendorSkuMapping (same flow as the WMS Flutter app)
  const [skuMappings, setSkuMappings] = useState<IVendorPurchaseCode[]>();
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const onCancel = useCallback(() => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate("/vendors");
    }
  }, [navigate]);

  useEffect(() => {
    if (id) {
      dispatch(getVendorById({ vendorId: id }));
      dispatch(getVendorSkuMappings(Number(id)))
        .unwrap()
        .then((mappings) =>
          setSkuMappings(
            (mappings ?? [])
              .filter((m) => m.purchaseSkuCode)
              .map((m) => ({
                purchaseSkuCode: m.purchaseSkuCode,
                relibilityScore: Number(m.reliabilityScore ?? 0),
              }))
          )
        )
        .catch(() => setSkuMappings([]));
    } else {
      navigate(routes.VENDOR.ALL);
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (status === "loading") {
      message.loading({ content: "Loading vendor...", key: MESSAGE_KEY });
    } else if (status === "error") {
      message.error({ content: error, key: MESSAGE_KEY });
    }
    return () => message.destroy();
  }, [status, error]);

  useEffect(() => {
    if (amendmentStatus === "saving") {
      message.loading({ content: "Submitting request...", key: MESSAGE_KEY });
    } else if (amendmentStatus === "error" && amendmentError) {
      message.error({ content: amendmentError, key: MESSAGE_KEY });
    }
    return () => message.destroy();
  }, [amendmentStatus, amendmentError]);

  useEffect(() => {
    const found = vendors?.data.find((v) => v.id.toString() === id);
    if (found) {
      const { address, metadata, email, ...rest } = found;
      setOriginalMetadata(metadata);

      // Codes stored on the vendor metadata (key "relibilityScore" per API contract);
      // the vendorSkuMapping API response takes precedence when it has entries.
      const metadataCodes: IVendorPurchaseCode[] = Array.isArray(metadata?.purchaseCodes)
        ? metadata!.purchaseCodes!
            .map((e: any) => ({
              purchaseSkuCode: e?.purchaseSkuCode?.toString() ?? "",
              relibilityScore: Number(e?.relibilityScore ?? e?.reliabilityScore ?? 0),
            }))
            .filter((e) => e.purchaseSkuCode)
        : [];

      setVendor({
        ...rest,
        vendorEmail: email,                           // server "email" → form "vendorEmail"
        address1: address?.address1,
        address2: address?.address2,
        city: VENDOR_CITY_OPTIONS.includes(address?.city) ? address?.city : undefined,
        state: address?.state,
        pincode: address?.pincode,
        accountNumber: metadata?.bankAccountDetails?.accountNumber,
        accountName: metadata?.bankAccountDetails?.accountName,
        bankName: metadata?.bankAccountDetails?.bankName,
        ifscCode: metadata?.bankAccountDetails?.ifscCode,
        branchName: metadata?.bankAccountDetails?.branchName,
        gstin: metadata?.gstin,
        market: metadata?.market,
        paymentMethod: metadata?.paymentMethod,
        parentVendorId: metadata?.parentVendorId,
        cancelledChequeFileUrl: metadata?.cancelledChequeFileUrl,
        shopImageUrl: metadata?.shopImageUrl,
        licenseFileUrl: metadata?.licenseFileUrl,
        gstCertificateFileUrl: metadata?.gstCertificateFileUrl,
        panImageUrl: metadata?.panImageUrl,
        adhaarImageUrl: metadata?.adhaarImageUrl,
        purchaseCodes: skuMappings?.length ? skuMappings : metadataCodes,
        latitude: toNumberOrUndefined(metadata?.latitude),
        longitude: toNumberOrUndefined(metadata?.longitude),
      });
    }
  }, [vendors, id, skuMappings]);

  const onFinish = (values: Record<string, any>) => {
    const {
      address1, address2, city, state, pincode,
      accountNumber, accountName, gstin, bankName, ifscCode, branchName,
      cancelledChequeFileUrl, shopImageUrl, licenseFileUrl,
      gstCertificateFileUrl, panImageUrl, adhaarImageUrl,
      paymentMethod, market, parentVendorId,
      purchaseCodes, latitude, longitude,
      vendorEmail,
      ...rest
    } = values;

    const payload = {
      ...rest,
      id: Number(id),
      email: vendorEmail || null,
      address: { address1, address2, city, state, pincode },
      metadata: {
        ...originalMetadata,
        gstin: gstin || null,
        bankAccountDetails: { accountNumber, accountName, bankName, ifscCode, branchName },
        market: market || null,
        paymentMethod: paymentMethod || null,
        cancelledChequeFileUrl: cancelledChequeFileUrl || null,
        shopImageUrl: shopImageUrl || null,
        licenseFileUrl: licenseFileUrl || null,
        gstCertificateFileUrl: gstCertificateFileUrl || null,
        panImageUrl: panImageUrl || null,
        adhaarImageUrl: adhaarImageUrl || null,
        purchaseCodes: Array.isArray(purchaseCodes) && purchaseCodes.length
          ? purchaseCodes.map((c: any) => ({ purchaseSkuCode: c.purchaseSkuCode, reliabilityScore: c.relibilityScore ?? c.reliabilityScore}))
          : null,
        parentVendorId: rest.vendorType === "CHILD" ? (parentVendorId || null) : null,
        latitude: latitude ?? null,
        longitude: longitude ?? null,
      },
      whId: getWarehouseId(),
    };

    dispatch(
      submitVendorAmendment({
        entityType: "VENDOR",
        entityId: id,
        requestType: "UPDATE",
        payload,
      })
    )
      .unwrap()
      .then(() => {
        message.success({ content: "Vendor update request submitted for approval!", key: MESSAGE_KEY });
        navigate("/vendors");
      })
      .catch(() => {});
  };

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Edit Vendor" />
        </Col>
      </Row>
      <Row>
        <VendorForm
          formInstance={form}
          values={vendor}
          editingVendorId={id}
          onCancel={onCancel}
          onFinish={onFinish}
          onSubmit={() => form.submit()}
          loading={amendmentStatus === "saving"}
        />
      </Row>
    </>
  );
};

export default EditVendorPage;
